import { useQuery } from '@tanstack/react-query';

import axiosInstance from '@/utils/httpClient';

export interface DashboardStats {
  plan: string;
  image_credits_used: number;
  /** null when the plan is unlimited (Infinity does not survive JSON). */
  images_in_plan: number | null;
  bonus_images: number | null;
}

/**
 * `/api/stats?dashboard=true`, shared by the dashboard's credits chip and the
 * sidebar's credits meter (one request when both are on screen).
 */
export function useDashboardStats(enabled: boolean) {
  return useQuery(
    ['dashboard-stats'],
    async () => {
      const { data } = await axiosInstance.get<DashboardStats>('/api/stats', {
        params: { dashboard: 'true' },
        skipErrorToast: true,
      });
      return data;
    },
    { enabled, staleTime: 60 * 1000, refetchOnWindowFocus: false, retry: 1 },
  );
}

/** Image credits left this period; `left` is null on unlimited plans. */
export function creditsLeft(data: DashboardStats): {
  left: number | null;
  fraction: number;
  low: boolean;
} {
  const inPlan = data.images_in_plan;
  if (inPlan === null || !Number.isFinite(Number(inPlan))) {
    return { left: null, fraction: 1, low: false };
  }
  const used = Number(data.image_credits_used) || 0;
  const total = Number(inPlan) + (Number(data.bonus_images) || 0);
  const left = Math.max(0, Math.floor(total - used));
  const fraction = total > 0 ? left / total : 0;
  return { left, fraction, low: fraction < 0.15 };
}
